import React, { useCallback, useEffect, useRef, useState } from "react";
import DOMPurify from "dompurify";
import {
  Bold, Italic, Underline, Heading2, List, AlignLeft, AlignCenter, AlignRight,
  Link2, Image as ImageIcon, Palette,
} from "lucide-react";
import { cn } from "../lib/utils";

const COLORS = [
  { name: "Default", value: "" },
  { name: "Emerald", value: "#10b981" },
  { name: "Slate",   value: "#0f172a" },
  { name: "Red",     value: "#e11d48" },
  { name: "Blue",    value: "#206bc4" },
  { name: "Amber",   value: "#f59e0b" },
  { name: "Violet",  value: "#7c3aed" },
];

function ToolbarBtn({ onClick, active, title, children }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      title={title}
      className={cn(
        "h-8 w-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors",
        active && "bg-primary/10 text-primary"
      )}
    >
      {children}
    </button>
  );
}

/**
 * Lightweight rich-text editor (contentEditable + execCommand).
 * Sufficient for Vigil News announcements — headline formatting, colours, links, lists.
 */
export default function RichTextEditor({ value, onChange, placeholder = "Details — add links, images, formatting…" }) {
  const ref = useRef(null);
  const [showColors, setShowColors] = useState(false);

  // Initial value — sanitize HTML with DOMPurify to prevent XSS on stored payloads
  useEffect(() => {
    if (ref.current) {
      const safe = DOMPurify.sanitize(value || "");
      if (ref.current.innerHTML !== safe) {
        ref.current.innerHTML = safe;
      }
    }
  }, [value]);

  const exec = useCallback((cmd, arg = null) => {
    document.execCommand(cmd, false, arg);
    ref.current?.focus();
    onChange?.(ref.current?.innerHTML || "");
  }, [onChange]);

  const insertLink = () => {
    const url = window.prompt("Link URL");
    if (!url) return;
    exec("createLink", url);
  };

  const insertImage = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (file.size > 512 * 1024) { window.alert("Image must be under 512 KB"); return; }
      const reader = new FileReader();
      reader.onload = () => exec("insertImage", reader.result);
      reader.readAsDataURL(file);
    };
    input.click();
  };

  const setColor = (c) => {
    setShowColors(false);
    if (!c) { exec("removeFormat"); return; }
    exec("foreColor", c);
  };

  return (
    <div className="rounded-lg border border-border bg-background overflow-hidden">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-border bg-muted/30 px-1 py-1">
        <ToolbarBtn title="Heading" onClick={() => exec("formatBlock", "h3")}><Heading2 className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn title="Bold" onClick={() => exec("bold")}><Bold className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn title="Italic" onClick={() => exec("italic")}><Italic className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn title="Underline" onClick={() => exec("underline")}><Underline className="w-4 h-4" /></ToolbarBtn>
        <span className="w-px h-5 bg-border mx-1" />
        <ToolbarBtn title="Bulleted list" onClick={() => exec("insertUnorderedList")}><List className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn title="Align left" onClick={() => exec("justifyLeft")}><AlignLeft className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn title="Align center" onClick={() => exec("justifyCenter")}><AlignCenter className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn title="Align right" onClick={() => exec("justifyRight")}><AlignRight className="w-4 h-4" /></ToolbarBtn>
        <span className="w-px h-5 bg-border mx-1" />
        <div className="relative">
          <ToolbarBtn title="Text colour" onClick={() => setShowColors((v) => !v)} active={showColors}><Palette className="w-4 h-4" /></ToolbarBtn>
          {showColors && (
            <div className="absolute top-full mt-1 z-20 rounded-lg border border-border bg-popover shadow-lg p-1.5 flex gap-1">
              {COLORS.map((c) => (
                <button
                  key={c.name}
                  type="button"
                  title={c.name}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setColor(c.value)}
                  className={cn("w-5 h-5 rounded-full border border-border", !c.value && "bg-background")}
                  style={c.value ? { backgroundColor: c.value } : undefined}
                />
              ))}
            </div>
          )}
        </div>
        <ToolbarBtn title="Link" onClick={insertLink}><Link2 className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn title="Image" onClick={insertImage}><ImageIcon className="w-4 h-4" /></ToolbarBtn>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={(e) => onChange?.(e.currentTarget.innerHTML)}
        data-placeholder={placeholder}
        className={cn(
          "min-h-[140px] p-3 text-[13.5px] leading-relaxed prose prose-sm max-w-none dark:prose-invert",
          "focus:outline-none [&_h3]:text-[15px] [&_h3]:font-semibold [&_h3]:mt-2 [&_h3]:mb-1",
          "[&_ul]:list-disc [&_ul]:pl-5 [&_a]:text-primary [&_a]:underline [&_img]:max-w-full [&_img]:rounded",
          "empty:before:content-[attr(data-placeholder)] empty:before:text-muted-foreground empty:before:pointer-events-none"
        )}
      />
    </div>
  );
}
